import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./createAccount.css";
import { createAccountFunction, sendOTPfunction } from "../../api/login_api";
import Swal from "sweetalert2";

const CreateAccount = () => {
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState("+91");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 10) value = value.slice(0, 10);
    setFormData((prev) => ({ ...prev, phoneNumber: value }));
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const { name, email, phoneNumber } = formData;

    // Validation
    if (!name.trim()) {
      Swal.fire({
        title: "Name Required",
        text: "Please enter your full name",
        icon: "warning",
      });
      return;
    }

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      Swal.fire({
        title: "Invalid Email",
        text: "Please enter a valid email address",
        icon: "warning",
      });
      return;
    }

    if (phoneNumber.length !== 10) {
      Swal.fire({
        title: "Invalid Phone",
        text: "Please enter a 10-digit phone number",
        icon: "warning",
      });
      return;
    }

    setIsLoading(true);

    try {
      // 1. Create user account in backend
      // Hardcoding PAN and Aadhaar to fulfill backend validation requirements
      const dummyPan = "1111";
      const dummyAadhar = "1111";

      const createRes = await createAccountFunction(
        name,
        email,
        phoneNumber,
        dummyPan,
        dummyAadhar
      );

      if (!createRes.success) {
        throw new Error(createRes.error || "Failed to create account");
      }

      console.log("Account created:", createRes.data);

      // 2. Send OTP
      const otpRes = await sendOTPfunction(phoneNumber);

      if (otpRes.success) {
        Swal.fire({
          title: "Account Created!",
          text: `Verification code sent to ${countryCode} ${phoneNumber}`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        // 3. Navigate to OTP page with registration flag
        navigate("/otp", {
          state: {
            Otp: otpRes.otp,
            phoneNumber: otpRes.phoneNumber,
            otpSent: true,
            isRegistration: true,
            userData: {
              name: name,
              email: email,
              phone: phoneNumber,
              pan: dummyPan,
              aadhar: dummyAadhar,
            },
          },
        });
      } else {
        throw new Error(otpRes.error || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Sign up error:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Something went wrong. Please try again.",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="wc-create-account-container">
      {/* Left Section - Branding */}
      <div className="wc-create-account-left-section">
        <div className="wc-create-account-decorative-shape shape-1"></div>
        <div className="wc-create-account-decorative-shape shape-2"></div>
        <div className="wc-create-account-content">
          <div className="wc-create-account-logo-wrapper">
            <div className="wc-create-account-logo-glass-card">
              <img
                src="/wealth-compounders.png"
                alt="Logo"
                className="wc-create-account-logo-img"
              />
            </div>
          </div>
          <h1 className="wc-create-account-title">Join Us</h1>
          <p className="wc-create-account-subtitle">
            Start your wealth creation journey today.
          </p>
          <div className="wc-create-account-footer-text">
            <span>Already have an account? </span>
            <button onClick={() => navigate("/")}>Sign In</button>
          </div>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="wc-create-account-right-section">
        <div className="wc-create-account-form-container">
          <h2 className="wc-create-account-form-title">Create Account</h2>
          <p className="wc-create-account-form-subtitle">
            Enter your details to get started.
          </p>

          <form onSubmit={handleSignUp}>
            {/* Name Input */}
            <div className="wc-create-account-form-group">
              <label className="wc-create-account-label">Full Name</label>
              <div className="wc-create-account-input-wrapper">
                <input
                  type="text"
                  name="name"
                  className="wc-create-account-text-input"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            {/* Email Input */}
            <div className="wc-create-account-form-group">
              <label className="wc-create-account-label">Email Address</label>
              <div className="wc-create-account-input-wrapper">
                <input
                  type="email"
                  name="email"
                  className="wc-create-account-text-input"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            {/* Phone Input */}
            <div className="wc-create-account-form-group">
              <label className="wc-create-account-label">Phone Number</label>
              <div className="wc-create-account-phone-input-container">
                <select
                  className="wc-create-account-country-code"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                </select>
                <input
                  type="tel"
                  className="wc-create-account-phone-input"
                  placeholder="Enter Phone Number"
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  maxLength="10"
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="wc-create-account-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? "Creating Account..." : "Verify & Create"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
